import Dropzone from "react-dropzone";
import { useSocialStore } from "../../../stores/social";
import { showToastError, showToastSuccess } from "../../../services/toast";
import upload from "../../../assets/icons/darkIcons/upload.svg";
import { normalizeFileName } from "../../../services/function";
import { getBulletin, getNumberOfBulletins, postBulletins, updateNumberOfBulletins } from "../../../api/social";
import FollowStatue from "./FollowStatue";
import { useUserContext } from "../../../contexts/userContext";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const UploadBulletins = ({
  month,
  customer,
}: {
  month: string;
  customer: any;
}) => {
  const { t } = useTranslation();
  // Context
  const { typeRole } = useUserContext();
  // Store
  const { updateSocial, setUpdateSocial, idCustomer, numberOfBulletins, setNumberOfBulletins  } = useSocialStore();

  useEffect(() => {
    getNumberOfBulletins(idCustomer)
      .then((res) => {
        if (res.data.numberofpages === null) {
          setNumberOfBulletins(0);
        } else {
          setNumberOfBulletins(res.data.numberofpages);
        }
      })
      .catch((err) => {
        showToastError(err.response.data.message);
      });
  }, []);

  // Permet d'uploader les bulletins
  const handleDrop = (acceptedFiles: File[]) => {
    // Vérifier qu'un seul fichier est sélectionné
    if (acceptedFiles.length > 1) {
      showToastError(t("Veuillez sélectionner un seul fichier à la fois"));
      return;
    }

    const file = acceptedFiles[0];
    const formData = new FormData();
    const normalizedFileName = normalizeFileName(file.name);
    formData.append("file", file, normalizedFileName);

    postBulletins(idCustomer, formData)
      .then((response) => {
        setUpdateSocial(!updateSocial);
        setNumberOfBulletins(response?.data?.numberofpages);
        showToastSuccess(t("Bulletin envoyé avec succès"));
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue lors de l'envoi du document"));
      });
  };

  // Permet de télécharger les bulletins
  const handleDownloadBulletins = (e: any) => {
    e.preventDefault();
    getBulletin(idCustomer)
      .then((res) => {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `bulletins_${idCustomer}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError(
          t("Une erreur est survenue lors de la récupération des bulletins")
        );
      });
  };

  // Permet de changer le nombre de bulletins
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNumberOfBulletins(Number(e.target.value));
  }

  const handleChangeNumberOfBulletins = () => {
    updateNumberOfBulletins(idCustomer, { numberofpages: numberOfBulletins })
      .then(() => {
        setUpdateSocial(!updateSocial);
        showToastSuccess(t("Nombre de bulletins modifié avec succès"));
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue lors de la modification du nombre de bulletins"));
      });
  }

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-full h-full overflow-hidden flex flex-col">
        {/* En-tête - Optimisé pour PC portable */}
        <div className="bg-thead px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-1.5">
              <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-black font-semibold text-base font-Nunito">
              {t("Bulletins de paie")}
            </h3>
          </div>
        </div>

        {/* Contenu scrollable - Compact */}
        <div className="flex-1 overflow-y-auto p-3 bg-gray-50 space-y-3">
          {/* Nombre de bulletins - Company uniquement - Compact */}
          {typeRole === "Company" && (
            <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 flex-1">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  <label htmlFor="numberOfBulletins" className="text-xs font-semibold text-gray-700">
                    {t("Nombre de bulletins")}
                  </label>
                </div>
                <input 
                  type="number" 
                  name="numberOfBulletins" 
                  id="numberOfBulletins" 
                  value={numberOfBulletins} 
                  onChange={(e) => handleChange(e)} 
                  className="w-16 px-2 py-1.5 text-center border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all duration-200 font-semibold text-xs" 
                />
                <button 
                  className="bg-linear-to-r cursor-pointer from-blue-500 to-blue-600 text-white rounded-lg px-3 py-1.5 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 font-medium text-xs flex items-center gap-1.5" 
                  onClick={() => handleChangeNumberOfBulletins()}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t("Modifier")}
                </button>
              </div>
            </div>
          )}

          {/* Zone principale des bulletins - Compact */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            {customer?.sc_BatchPaySlipName === null ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="bg-gray-100 rounded-full p-4 mb-3">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="text-gray-700 font-semibold text-sm mb-1">
                  {t("Aucun bulletin déposé")}
                </h4>
                <p className="text-gray-500 text-xs">
                  {t("Les bulletins apparaîtront ici une fois déposés")}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-linear-to-r   from-indigo-50 to-blue-50 rounded-lg p-3 border border-indigo-100">
                  <div className="flex items-start gap-2 mb-2">
                    <div className="bg-blue-500 rounded-lg p-1.5 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-gray-800 font-bold text-sm mb-0.5 font-Nunito">
                        {t("Bulletins de")} {month}
                      </h4>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="truncate">{t("Dernier dépôt :")} {customer?.sc_BatchPaySlipDate}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="w-full cursor-pointer bg-linear-to-r from-blue-500 to-blue-600 text-white py-2 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-1.5 font-medium text-xs"
                    onClick={(e) => handleDownloadBulletins(e)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t("Télécharger les bulletins")}
                  </button>
                </div>
              </div>
            )}

            {/* Zone de dépôt - Company uniquement - Compact */}
            {typeRole === "Company" && (
              <div className="mt-4">
                <Dropzone onDrop={handleDrop}>
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-lg p-5 cursor-pointer transition-all duration-200 ${
                        isDragActive
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/50"
                      }`}
                    >
                      <input {...getInputProps()} />
                      <div className="flex flex-col items-center gap-2">
                        <div className={`rounded-full p-3 ${isDragActive ? "bg-indigo-100" : "bg-gray-200"}`}>
                          <img src={upload} alt="upload" className="w-6 h-6" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold text-gray-700 mb-0.5">
                            {isDragActive ? t("Déposez le fichier ici") : t("Glissez-déposez votre fichier")}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {t("ou cliquez pour sélectionner")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </Dropzone>
              </div>
            )}
          </div>

          {/* Statut de suivi - Compact */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden md:block hidden">
            <FollowStatue />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadBulletins;
